import React, {
    useContext,
    createContext,
    useReducer,
    useMemo,
    FC
} from 'react'
import type { CustomerPaymentTokens } from '@pylot-data/pylotschema'

type SignUpDetail = {
    firstname: string
    lastname: string
}

type PageType = {
    transparentHeader: boolean
    isNicotinePage: boolean
}

type AddToCartPayload = {
    cmsBlockId: string
    isShowPopup: boolean
    hideContent: boolean
    product?: {
        name: string
        image: string
    }
}

export interface State {
    scrollToReview?: boolean
    signUpDetail?: SignUpDetail | null
    isKlarnaAuthorized?: boolean
    toggleYotpoReviewPageWidget?: boolean
    toggleCloseInspectedDesktopMenuItem?: boolean
    showLocationModal?: boolean
    addToCartPopupPayload?: AddToCartPayload | null
    isSocialLoading?: boolean
    isGuestAddressFormVisible?: boolean
    isCreditSelected?: string
    savedCards?: CustomerPaymentTokens | null
    isNicotinePage?: boolean
    bundleStep?: number
    isCookiesAccepted?: boolean
    gtmLocation?: string
    isStoreLocatorEnabled?: boolean
    pageType?: PageType
    isSpreedlyFatalError?: boolean
}

interface Values extends State {
    setScrollToReview: (scrollToReview?: boolean) => void
    setSignUpDetail: (signUpDetail?: SignUpDetail | null) => void
    setIsKlarnaAuthorized: (isKlarnaAuthorized?: boolean) => void
    setToggleReinitYotpoReviewPageWidget: (
        toggleYotpoReviewPageWidget?: boolean
    ) => void
    setToggleCloseInspectedDesktopMenuItem: (
        toggleCloseInspectedDesktopMenuItem: boolean
    ) => void
    setShowLocationModal: (showLocationModal?: boolean) => void
    setAddToCartPopupPayload: (
        addToCartPopupPayload?: AddToCartPayload | null
    ) => void
    setSocialLoader: (isSocialLoading?: boolean) => void
    setGuestAddressFormVisible: (isGuestAddressFormVisible?: boolean) => void
    setIsCreditSelected: (isCreditSelected?: string) => void
    setSavedCards: (savedCards?: CustomerPaymentTokens | null) => void
    setIsNicotinePage: (isNicotinePage?: boolean) => void
    setBundleStep: (step: number) => void
    setIsCookiesAccepted: (isCookiesAccepted?: boolean) => void
    setIsStoreLocatorEnabled: (isStoreLocatorEnabled?: boolean) => void
    setPageType: (pageType?: PageType) => void
    setIsSpreedlyFatalError: (isSpreedlyFatalError: boolean) => void
}

interface Action extends State {
    type:
        | 'SCROLL_TO_REVIEW'
        | 'SIGN_UP_DETAIL'
        | 'IS_KLARNA_AUTHORISED'
        | 'REINIT_YOTPO_REVIEW_PAGE_WIDGET'
        | 'CLOSE_INSPECTED_DESKTOP_MENU_ITEM'
        | 'SHOW_LOCATION_MODAL'
        | 'ADD_TO_CART_POPUP_PAYLOAD'
        | 'SOCIAL_LOGIN_LOADER'
        | 'IS_GUEST_ADDRESS_FORM_VISIBLE'
        | 'IS_CREDIT_SELECTED'
        | 'SAVE_CARDS'
        | 'IS_NICOTINE_PAGE'
        | 'BUNDLE_STEP'
        | 'IS_COOKIES_ACCEPTED'
        | 'SET_GTM_LOCATION'
        | 'SET_STORE_LOCATOR_DATA'
        | 'PAGE_TYPE'
        | 'IS_SPREEDLY_FATAL_ERROR'
}

const initialState = {
    scrollToReview: false,
    signUpDetail: null,
    isKlarnaAuthorized: false,
    toggleYotpoReviewPageWidget: false,
    toggleCloseInspectedDesktopMenuItem: false,
    showLocationModal: false,
    addToCartPopupPayload: null,
    isGuestAddressFormVisible: false,
    isCreditSelected: '',
    isSocialLoading: false,
    savedCards: null,
    isNicotinePage: true,
    bundleStep: 0,
    isCookiesAccepted: true,
    gtmLocation: '',
    isStoreLocatorEnabled: false,
    pageType: {
        transparentHeader: false,
        isNicotinePage: false
    },
    isSpreedlyFatalError: false
}

const authReducer = (state: State, action: Action) => {
    switch (action.type) {
        case 'SCROLL_TO_REVIEW': {
            return {
                ...state,
                scrollToReview: action.scrollToReview
            }
        }
        case 'SIGN_UP_DETAIL': {
            return {
                ...state,
                signUpDetail: action.signUpDetail
            }
        }
        case 'IS_KLARNA_AUTHORISED': {
            return {
                ...state,
                isKlarnaAuthorized: action.isKlarnaAuthorized
            }
        }
        case 'REINIT_YOTPO_REVIEW_PAGE_WIDGET': {
            return {
                ...state,
                toggleYotpoReviewPageWidget: action.toggleYotpoReviewPageWidget
            }
        }
        case 'CLOSE_INSPECTED_DESKTOP_MENU_ITEM': {
            return {
                ...state,
                toggleCloseInspectedDesktopMenuItem:
                    action.toggleCloseInspectedDesktopMenuItem
            }
        }
        case 'SHOW_LOCATION_MODAL': {
            return {
                ...state,
                showLocationModal: action.showLocationModal
            }
        }
        case 'ADD_TO_CART_POPUP_PAYLOAD': {
            return {
                ...state,
                addToCartPopupPayload: action.addToCartPopupPayload
            }
        }
        case 'SOCIAL_LOGIN_LOADER': {
            return {
                ...state,
                isSocialLoading: action.isSocialLoading
            }
        }
        case 'IS_GUEST_ADDRESS_FORM_VISIBLE': {
            return {
                ...state,
                isGuestAddressFormVisible: action.isGuestAddressFormVisible
            }
        }
        case 'IS_CREDIT_SELECTED': {
            return {
                ...state,
                isCreditSelected: action.isCreditSelected
            }
        }
        case 'SAVE_CARDS': {
            return {
                ...state,
                savedCards: action.savedCards
            }
        }
        case 'IS_NICOTINE_PAGE': {
            return {
                ...state,
                isNicotinePage: action.isNicotinePage
            }
        }
        case 'BUNDLE_STEP': {
            return {
                ...state,
                bundleStep: action.bundleStep
            }
        }
        case 'IS_COOKIES_ACCEPTED': {
            return { ...state, isCookiesAccepted: action.isCookiesAccepted }
        }
        case 'SET_GTM_LOCATION': {
            return { ...state, gtmLocation: action.gtmLocation }
        }
        case 'SET_STORE_LOCATOR_DATA': {
            return {
                ...state,
                isStoreLocatorEnabled: action.isStoreLocatorEnabled
            }
        }
        case 'PAGE_TYPE': {
            return {
                ...state,
                pageType: action.pageType
            }
        }
        case 'IS_SPREEDLY_FATAL_ERROR': {
            return {
                ...state,
                isSpreedlyFatalError: action.isSpreedlyFatalError
            }
        }
    }
}

const ReviewContext = createContext(initialState as Values)

export const useAlfakherContext = () => useContext(ReviewContext)

export const AlfakherContext: FC = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState)
    const setScrollToReview = (scrollToReview = true) =>
        dispatch({
            type: 'SCROLL_TO_REVIEW',
            scrollToReview
        })

    const setIsKlarnaAuthorized = (isKlarnaAuthorized: boolean) =>
        dispatch({
            type: 'IS_KLARNA_AUTHORISED',
            isKlarnaAuthorized
        })

    const setSignUpDetail = (signUpDetail: SignUpDetail) =>
        dispatch({
            type: 'SIGN_UP_DETAIL',
            signUpDetail
        })

    const setToggleReinitYotpoReviewPageWidget = (
        toggleYotpoReviewPageWidget = true
    ) =>
        dispatch({
            type: 'REINIT_YOTPO_REVIEW_PAGE_WIDGET',
            toggleYotpoReviewPageWidget
        })

    const setToggleCloseInspectedDesktopMenuItem = (
        toggleCloseInspectedDesktopMenuItem: boolean
    ) =>
        dispatch({
            type: 'CLOSE_INSPECTED_DESKTOP_MENU_ITEM',
            toggleCloseInspectedDesktopMenuItem
        })

    const setShowLocationModal = (showLocationModal: boolean) =>
        dispatch({
            type: 'SHOW_LOCATION_MODAL',
            showLocationModal
        })
    const setAddToCartPopupPayload = (
        addToCartPopupPayload: AddToCartPayload
    ) =>
        dispatch({
            type: 'ADD_TO_CART_POPUP_PAYLOAD',
            addToCartPopupPayload
        })
    const setSocialLoader = (isSocialLoading = true) =>
        dispatch({
            type: 'SOCIAL_LOGIN_LOADER',
            isSocialLoading
        })

    const setGuestAddressFormVisible = (isGuestAddressFormVisible: boolean) =>
        dispatch({
            type: 'IS_GUEST_ADDRESS_FORM_VISIBLE',
            isGuestAddressFormVisible
        })
    const setIsCreditSelected = (isCreditSelected: string) =>
        dispatch({
            type: 'IS_CREDIT_SELECTED',
            isCreditSelected
        })

    const setSavedCards = (savedCards: CustomerPaymentTokens) =>
        dispatch({
            type: 'SAVE_CARDS',
            savedCards
        })

    const setIsNicotinePage = (isNicotinePage: boolean) =>
        dispatch({
            type: 'IS_NICOTINE_PAGE',
            isNicotinePage
        })
    const setBundleStep = (step: number) => {
        dispatch({
            type: 'BUNDLE_STEP',
            bundleStep: step
        })
    }
    const setIsCookiesAccepted = (isCookiesAccepted?: boolean) => {
        dispatch({ type: 'IS_COOKIES_ACCEPTED', isCookiesAccepted })
    }

    const setGTMLocation = (gtmLocation: string) => {
        dispatch({ type: 'SET_GTM_LOCATION', gtmLocation })
    }

    const setIsStoreLocatorEnabled = (isStoreLocatorEnabled: boolean) => {
        dispatch({ type: 'SET_STORE_LOCATOR_DATA', isStoreLocatorEnabled })
    }

    const setPageType = (pageType: PageType) =>
        dispatch({ type: 'PAGE_TYPE', pageType })

    const setIsSpreedlyFatalError = (isSpreedlyFatalError: boolean) =>
        dispatch({ type: 'IS_SPREEDLY_FATAL_ERROR', isSpreedlyFatalError })

    const value = useMemo(
        () => ({
            ...state,
            setScrollToReview,
            setSignUpDetail,
            setIsKlarnaAuthorized,
            setToggleReinitYotpoReviewPageWidget,
            setToggleCloseInspectedDesktopMenuItem,
            setShowLocationModal,
            setAddToCartPopupPayload,
            setGuestAddressFormVisible,
            setIsCreditSelected,
            setSocialLoader,
            setSavedCards,
            setIsNicotinePage,
            setBundleStep,
            setIsCookiesAccepted,
            setGTMLocation,
            setIsStoreLocatorEnabled,
            setPageType,
            setIsSpreedlyFatalError
        }),
        [state]
    )
    return (
        <ReviewContext.Provider value={value as Values}>
            {children}
        </ReviewContext.Provider>
    )
}
